import type { Camera, City } from '../types'

/**
 * Thematic collections of public cameras — the "interesting cameras to tap into"
 * list, grouped by subject rather than location.
 *
 * Every camera here is an operator-published YouTube live stream that was
 * confirmed live AND publicly embeddable on the `verifiedAt` date by
 * `npm run check:cams`. These are feeds their operators (aquariums, zoos,
 * wildlife trusts, city cams, beach cams) put out for anyone to watch — not
 * cameras found by scanning for weak security.
 *
 * Streams do end and operators do restart broadcasts (which mints a new video
 * id). Re-run the health check to see what has gone dark, then find the current
 * id on the same operator's channel.
 */

const yt = (videoId: string): Camera['source'] => ({ kind: 'youtube', videoId })
const watch = (videoId: string) => `https://www.youtube.com/watch?v=${videoId}`

export const collections: City[] = [
  {
    id: 'zoos',
    name: 'Zoos & Aquariums',
    country: 'Worldwide',
    countryCode: '',
    kind: 'collection',
    icon: '🦭',
    blurb: 'Otters, penguins, pandas, sharks and jellies — live from aquariums and zoos.',
  },
  {
    id: 'wildlife',
    name: 'Wildlife & Safari',
    country: 'Worldwide',
    countryCode: '',
    kind: 'collection',
    icon: '🐘',
    blurb: 'African waterholes, Alaskan bears, eagles and wolves, streaming 24/7 from the wild.',
  },
  {
    id: 'landmarks',
    name: 'Cities & Landmarks',
    country: 'Worldwide',
    countryCode: '',
    kind: 'collection',
    icon: '🏙️',
    blurb: 'Times Square, the canals of Venice, and other places that never sleep.',
  },
  {
    id: 'beaches',
    name: 'Beaches & Coasts',
    country: 'Worldwide',
    countryCode: '',
    kind: 'collection',
    icon: '🏖️',
    blurb: 'Caribbean sand, surf breaks, and the famous plane-spotting beach at St. Maarten.',
  },
  {
    id: 'airports',
    name: 'Airports & Planespotting',
    country: 'Worldwide',
    countryCode: '',
    kind: 'collection',
    icon: '✈️',
    blurb: 'Runway cams and 24/7 plane-spotting from airports around the world.',
  },
]

export const collectionCameras: Camera[] = [
  // ------------------------------------------------------------------ Zoos --
  {
    id: 'z-mba-otters',
    cityId: 'zoos',
    name: 'Sea Otters — Monterey Bay Aquarium',
    operator: 'Monterey Bay Aquarium',
    pageUrl: watch('abbR-Ttd-cA'),
    coords: [36.6182, -121.9018],
    tags: ['otter', 'aquarium'],
    source: yt('abbR-Ttd-cA'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'z-mba-jellies',
    cityId: 'zoos',
    name: 'Jellies — Monterey Bay Aquarium',
    operator: 'Monterey Bay Aquarium',
    pageUrl: watch('eQ_foBERmzA'),
    coords: [36.6182, -121.9018],
    tags: ['jellyfish', 'aquarium'],
    source: yt('eQ_foBERmzA'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'z-mba-kelp',
    cityId: 'zoos',
    name: 'Kelp Forest — Monterey Bay Aquarium',
    operator: 'Monterey Bay Aquarium',
    pageUrl: watch('w3LjpFhySTg'),
    coords: [36.6182, -121.9018],
    tags: ['kelp', 'aquarium'],
    source: yt('w3LjpFhySTg'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'z-mba-sharks',
    cityId: 'zoos',
    name: 'Sharks — Monterey Bay Aquarium',
    operator: 'Monterey Bay Aquarium',
    pageUrl: watch('tEtg5Kg3voQ'),
    coords: [36.6182, -121.9018],
    tags: ['shark', 'aquarium'],
    source: yt('tEtg5Kg3voQ'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'z-aop-penguins',
    cityId: 'zoos',
    name: 'Penguin Beach — Aquarium of the Pacific',
    operator: 'Aquarium of the Pacific (via explore.org)',
    pageUrl: watch('GSxpCbXsvtI'),
    coords: [33.7623, -118.1966],
    tags: ['penguin', 'aquarium'],
    source: yt('GSxpCbXsvtI'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'z-aop-sharks',
    cityId: 'zoos',
    name: 'Shark Lagoon — Aquarium of the Pacific',
    operator: 'Aquarium of the Pacific (via explore.org)',
    pageUrl: watch('YT7lH6U68S4'),
    coords: [33.7623, -118.1966],
    tags: ['shark', 'aquarium'],
    source: yt('YT7lH6U68S4'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'z-calacademy-penguins',
    cityId: 'zoos',
    name: 'Penguins — California Academy of Sciences',
    operator: 'California Academy of Sciences',
    pageUrl: watch('gy68wGk_AWY'),
    coords: [37.7699, -122.4661],
    tags: ['penguin', 'aquarium'],
    source: yt('gy68wGk_AWY'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'z-georgia-whalesharks',
    cityId: 'zoos',
    name: 'Ocean Voyager whale sharks — Georgia Aquarium',
    operator: 'Georgia Aquarium',
    pageUrl: watch('8WTFWvePutc'),
    coords: [33.7634, -84.3951],
    tags: ['whale shark', 'aquarium'],
    source: yt('8WTFWvePutc'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'z-seattle-otters',
    cityId: 'zoos',
    name: 'Sea Otters & Fur Seals — Seattle Aquarium',
    operator: 'Seattle Aquarium',
    pageUrl: watch('NqOmHpwMUxs'),
    coords: [47.6073, -122.3425],
    tags: ['otter', 'aquarium'],
    source: yt('NqOmHpwMUxs'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'z-vancouver-otters',
    cityId: 'zoos',
    name: 'Sea Otters — Vancouver Aquarium',
    operator: 'Vancouver Aquarium',
    pageUrl: watch('9mg9PoFEX2U'),
    coords: [49.3004, -123.131],
    tags: ['otter', 'aquarium'],
    source: yt('9mg9PoFEX2U'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'z-chengdu-pandas',
    cityId: 'zoos',
    name: 'Giant Pandas — Chengdu Panda Base',
    operator: 'iPanda',
    pageUrl: watch('SUXPnIEpbn4'),
    coords: [30.7333, 104.0667],
    tags: ['panda', 'zoo'],
    source: yt('SUXPnIEpbn4'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'z-honolulu-elephants',
    cityId: 'zoos',
    name: 'Elephants — Honolulu Zoo',
    operator: 'Honolulu Zoo',
    pageUrl: watch('WIKE2KSm8xc'),
    coords: [21.2707, -157.8215],
    tags: ['elephant', 'zoo'],
    source: yt('WIKE2KSm8xc'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'z-kc-polarbear',
    cityId: 'zoos',
    name: 'Polar Bear — Kansas City Zoo',
    operator: 'Kansas City Zoo & Aquarium',
    pageUrl: watch('BSUnBPvX9K4'),
    coords: [39.0119, -94.5236],
    tags: ['polar bear', 'zoo'],
    source: yt('BSUnBPvX9K4'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'z-stlouis-penguins',
    cityId: 'zoos',
    name: 'Penguins — Saint Louis Zoo',
    operator: 'Saint Louis Zoo',
    pageUrl: watch('hHLlZhianIQ'),
    coords: [38.635, -90.2894],
    tags: ['penguin', 'zoo'],
    source: yt('hHLlZhianIQ'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'z-pittsburgh-penguins',
    cityId: 'zoos',
    name: 'Penguins — Pittsburgh Zoo & Aquarium',
    operator: 'Pittsburgh Zoo (via Ozolio)',
    pageUrl: watch('cTi5sCsUSfc'),
    coords: [40.4859, -79.9209],
    tags: ['penguin', 'zoo'],
    source: yt('cTi5sCsUSfc'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'z-moody-penguins',
    cityId: 'zoos',
    name: 'Penguins — Moody Gardens',
    operator: 'Moody Gardens',
    pageUrl: watch('HHp4rjhJsWI'),
    coords: [29.273, -94.8511],
    tags: ['penguin', 'aquarium'],
    source: yt('HHp4rjhJsWI'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'z-staugustine-gators',
    cityId: 'zoos',
    name: 'St. Augustine Alligator Farm',
    operator: 'St. Augustine Alligator Farm',
    pageUrl: watch('LHtzZf4T7xw'),
    coords: [29.871, -81.287],
    tags: ['alligator', 'zoo'],
    source: yt('LHtzZf4T7xw'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'z-coral-city-miami',
    cityId: 'zoos',
    name: 'Coral City Camera — Miami',
    operator: 'Coral Morphologic',
    pageUrl: watch('7i8ARjIeM2k'),
    coords: [25.7907, -80.1918],
    tags: ['reef', 'underwater'],
    source: yt('7i8ARjIeM2k'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'z-baltimore-aquarium',
    cityId: 'zoos',
    name: 'National Aquarium — Baltimore',
    operator: 'EarthCam',
    pageUrl: watch('KSxc-N67TU4'),
    coords: [39.2851, -76.6083],
    tags: ['aquarium'],
    source: yt('KSxc-N67TU4'),
    verifiedAt: '2026-08-10',
  },

  // -------------------------------------------------------------- Wildlife --
  {
    id: 'w-brooks-falls',
    cityId: 'wildlife',
    name: 'Brooks Falls Bears — Katmai, Alaska',
    operator: 'explore.org',
    pageUrl: watch('J7ZrIDvqlic'),
    coords: [58.5561, -155.7797],
    tags: ['bear', 'nature'],
    source: yt('J7ZrIDvqlic'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'w-tembe-elephants',
    cityId: 'wildlife',
    name: 'Tembe Elephant Park — South Africa',
    operator: 'explore.org',
    pageUrl: watch('0P_LBKqVbfs'),
    coords: [-27.0333, 32.4167],
    tags: ['elephant', 'safari'],
    source: yt('0P_LBKqVbfs'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'w-africam-wildafrica',
    cityId: 'wildlife',
    name: 'Wild Africa 24/7 Safari',
    operator: 'Africam',
    pageUrl: watch('qpukdDslCjk'),
    coords: [-24.0, 31.5],
    tags: ['safari', 'nature'],
    source: yt('qpukdDslCjk'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'w-zambezi-tembo',
    cityId: 'wildlife',
    name: 'Zambezi River — Tembo Plains',
    operator: 'Africam',
    pageUrl: watch('jH3e5w4BWGA'),
    coords: [-15.6, 29.4],
    tags: ['safari', 'river'],
    source: yt('jH3e5w4BWGA'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'w-kalahari-waterhole',
    cityId: 'wildlife',
    name: 'Kalahari Waterhole — Jack’s Camp',
    operator: 'Africam',
    pageUrl: watch('jG0uYl5S44E'),
    coords: [-20.5, 21.7],
    tags: ['safari', 'waterhole'],
    source: yt('jG0uYl5S44E'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'w-maasai-mara',
    cityId: 'wildlife',
    name: 'Maasai Mara — Bushtops, Kenya',
    operator: 'Bushtops Safaris',
    pageUrl: watch('xXZqU5vnEug'),
    coords: [-1.4, 35.1],
    tags: ['safari', 'nature'],
    source: yt('xXZqU5vnEug'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'w-oldonyo-kenya',
    cityId: 'wildlife',
    name: 'ol Donyo Lodge — Kenya',
    operator: 'Africam',
    pageUrl: watch('XsOU8JnEpNM'),
    coords: [-2.3, 37.8],
    tags: ['safari', 'waterhole'],
    source: yt('XsOU8JnEpNM'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'w-vicfalls-waterhole',
    cityId: 'wildlife',
    name: 'Victoria Falls Safari Lodge Waterhole',
    operator: 'Africam',
    pageUrl: watch('f9uqHbXChno'),
    coords: [-17.9, 25.83],
    tags: ['safari', 'waterhole'],
    source: yt('f9uqHbXChno'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'w-wolf-center',
    cityId: 'wildlife',
    name: 'International Wolf Center — Minnesota',
    operator: 'explore.org',
    pageUrl: watch('5e4lsEe4Vew'),
    coords: [47.9, -91.86],
    tags: ['wolf', 'nature'],
    source: yt('5e4lsEe4Vew'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'w-bigbear-eagles',
    cityId: 'wildlife',
    name: 'Big Bear Bald Eagle Nest — California',
    operator: 'FOBBV CAM',
    pageUrl: watch('B4-L2nfGcuE'),
    coords: [34.24, -116.9],
    tags: ['eagle', 'nest'],
    source: yt('B4-L2nfGcuE'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'w-namib-desert',
    cityId: 'wildlife',
    name: 'Namib Desert — Namibia',
    operator: 'NamibiaCam',
    pageUrl: watch('ydYDqZQpim8'),
    coords: [-24.75, 15.28],
    tags: ['desert', 'waterhole'],
    source: yt('ydYDqZQpim8'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'w-bonobo-nursery',
    cityId: 'wildlife',
    name: 'Bonobo Nursery — Lola ya Bonobo, DR Congo',
    operator: 'explore.org',
    pageUrl: watch('mquay4ouPHU'),
    coords: [-4.55, 15.35],
    tags: ['bonobo', 'sanctuary'],
    source: yt('mquay4ouPHU'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'w-grace-gorillas',
    cityId: 'wildlife',
    name: 'GRACE Gorillas — DR Congo',
    operator: 'GRACE (via explore.org)',
    pageUrl: watch('672cUSe69J0'),
    coords: [-0.6, 29.5],
    tags: ['gorilla', 'sanctuary'],
    source: yt('672cUSe69J0'),
    verifiedAt: '2026-08-10',
  },

  // ------------------------------------------------------------- Landmarks --
  {
    id: 'l-times-square',
    cityId: 'landmarks',
    name: 'Times Square — New York',
    operator: 'EarthCam',
    pageUrl: watch('z-jYdOIKcTQ'),
    coords: [40.758, -73.9855],
    tags: ['city', 'landmark'],
    source: yt('z-jYdOIKcTQ'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'l-wtc',
    cityId: 'landmarks',
    name: 'World Trade Center — New York',
    operator: 'EarthCam',
    pageUrl: watch('5C9oM7C2Q9k'),
    coords: [40.7127, -74.0134],
    tags: ['city', 'landmark'],
    source: yt('5C9oM7C2Q9k'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'l-coney-island',
    cityId: 'landmarks',
    name: 'Coney Island — New York',
    operator: 'EarthCam',
    pageUrl: watch('H67j7H-7QD0'),
    coords: [40.5755, -73.9707],
    tags: ['boardwalk', 'beach'],
    source: yt('H67j7H-7QD0'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'l-bryant-park',
    cityId: 'landmarks',
    name: 'Bryant Park — New York',
    operator: 'Bryant Park NYC',
    pageUrl: watch('X7pAb0qjvf0'),
    coords: [40.7536, -73.9832],
    tags: ['park', 'city'],
    source: yt('X7pAb0qjvf0'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'l-dublin',
    cityId: 'landmarks',
    name: 'Temple Bar — Dublin',
    operator: 'EarthCam',
    pageUrl: watch('3nyPER2kzqk'),
    coords: [53.345, -6.2637],
    tags: ['city', 'street'],
    source: yt('3nyPER2kzqk'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'l-venice-rialto',
    cityId: 'landmarks',
    name: 'Rialto Bridge — Venice',
    operator: 'I Love You Venice',
    pageUrl: watch('Kmf_wiTFuXY'),
    coords: [45.438, 12.3358],
    tags: ['canal', 'landmark'],
    source: yt('Kmf_wiTFuXY'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'l-venice-grand-canal',
    cityId: 'landmarks',
    name: 'Grand Canal — Venice',
    operator: 'I Love You Venice',
    pageUrl: watch('r8N6IsoIkTk'),
    coords: [45.4408, 12.3155],
    tags: ['canal', 'city'],
    source: yt('r8N6IsoIkTk'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'l-venice-san-marco',
    cityId: 'landmarks',
    name: 'St. Mark’s Basin — Venice',
    operator: 'I Love You Venice',
    pageUrl: watch('7-XBRpKSv4Q'),
    coords: [45.434, 12.3388],
    tags: ['waterfront', 'landmark'],
    source: yt('7-XBRpKSv4Q'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'l-jackson-hole',
    cityId: 'landmarks',
    name: 'Town Square — Jackson Hole, Wyoming',
    operator: 'See Jackson Hole',
    pageUrl: watch('1EiC9bvVGnk'),
    coords: [43.4799, -110.7624],
    tags: ['town', 'street'],
    source: yt('1EiC9bvVGnk'),
    verifiedAt: '2026-08-10',
  },

  // --------------------------------------------------------------- Beaches --
  {
    id: 'b-maho-beach',
    cityId: 'beaches',
    name: 'Maho Beach — St. Maarten (plane spotting)',
    operator: 'SHOWME Caribbean',
    pageUrl: watch('iSeH45R-8R0'),
    coords: [18.041, -63.113],
    tags: ['beach', 'planes'],
    source: yt('iSeH45R-8R0'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'b-eagle-beach',
    cityId: 'beaches',
    name: 'Eagle Beach — Aruba',
    operator: 'SHOWME Caribbean',
    pageUrl: watch('_ZXMjk5K0_s'),
    coords: [12.5563, -70.0522],
    tags: ['beach', 'caribbean'],
    source: yt('_ZXMjk5K0_s'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'b-fort-lauderdale',
    cityId: 'beaches',
    name: 'Fort Lauderdale Beach — Florida',
    operator: 'PTZtv',
    pageUrl: watch('pXx3YQVSUGg'),
    coords: [26.1224, -80.1055],
    tags: ['beach'],
    source: yt('pXx3YQVSUGg'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'b-key-west',
    cityId: 'beaches',
    name: 'Key West Harbor — Florida',
    operator: 'PTZtv',
    pageUrl: watch('iHJbTrQSZys'),
    coords: [24.5551, -81.8],
    tags: ['harbour', 'coast'],
    source: yt('iHJbTrQSZys'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'b-koh-samui',
    cityId: 'beaches',
    name: 'Koh Samui — Thailand',
    operator: 'The Real Samui Webcam',
    pageUrl: watch('Tpj0cmMVOd0'),
    coords: [9.512, 100.0136],
    tags: ['beach', '4K'],
    source: yt('Tpj0cmMVOd0'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'b-surfline',
    cityId: 'beaches',
    name: 'Surfline — Live Surf Cams',
    operator: 'Surfline',
    pageUrl: watch('hm9iAviOZ20'),
    tags: ['surf', 'coast'],
    source: yt('hm9iAviOZ20'),
    verifiedAt: '2026-08-10',
  },

  // ------------------------------------------------------- Airports ----------
  // Only permanent 24/7 streams — no date-stamped daily broadcasts (their video
  // ids die the next day) and no "RE-Live" replays.
  {
    id: 'ap-sxm-maho',
    cityId: 'airports',
    name: 'Sint Maarten (SXM) — Maho Beach approach',
    description: 'The famous low approach over Maho Beach at Princess Juliana airport.',
    operator: 'Flightradar24',
    pageUrl: watch('2IQmpCXbOmM'),
    coords: [18.041, -63.1089],
    tags: ['airport', 'planespotting', 'iconic'],
    source: yt('2IQmpCXbOmM'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'ap-madeira',
    cityId: 'airports',
    name: 'Madeira (LPMA) — Cristiano Ronaldo airport',
    description: 'Landings and takeoffs at one of the world’s trickiest runway approaches.',
    operator: 'Madeira Airport Live',
    pageUrl: watch('BlDJpjn_0bM'),
    coords: [32.6979, -16.7745],
    tags: ['airport', 'planespotting', 'iconic'],
    source: yt('BlDJpjn_0bM'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'ap-haneda',
    cityId: 'airports',
    name: 'Tokyo Haneda (HND)',
    operator: 'Nippon TV News24',
    pageUrl: watch('3w4zEyFamuA'),
    coords: [35.5494, 139.7798],
    tags: ['airport', 'planespotting'],
    source: yt('3w4zEyFamuA'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'ap-lax',
    cityId: 'airports',
    name: 'Los Angeles (LAX) — runways 24L/24R',
    operator: 'AirlineVideosLive',
    pageUrl: watch('n4I0d44oBEs'),
    coords: [33.9416, -118.4085],
    tags: ['airport', 'planespotting'],
    source: yt('n4I0d44oBEs'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'ap-sfo',
    cityId: 'airports',
    name: 'San Francisco (SFO)',
    operator: 'Luftra Aviation',
    pageUrl: watch('QvRZgOmBX3Q'),
    coords: [37.6213, -122.379],
    tags: ['airport', 'planespotting', 'ATC'],
    source: yt('QvRZgOmBX3Q'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'ap-seattle',
    cityId: 'airports',
    name: 'Seattle–Tacoma (SEA)',
    operator: 'Rainier Aviation',
    pageUrl: watch('KqyOMgIbHIM'),
    coords: [47.4502, -122.3088],
    tags: ['airport', 'planespotting', '4K'],
    source: yt('KqyOMgIbHIM'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'ap-lasvegas',
    cityId: 'airports',
    name: 'Las Vegas (LAS) — Harry Reid Intl',
    operator: 'Flightradar24',
    pageUrl: watch('V7_orOtu-oo'),
    coords: [36.084, -115.1537],
    tags: ['airport', 'planespotting'],
    source: yt('V7_orOtu-oo'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'ap-laguardia',
    cityId: 'airports',
    name: 'New York LaGuardia (LGA)',
    operator: 'Flightradar24',
    pageUrl: watch('OUBslrCqREs'),
    coords: [40.7769, -73.874],
    tags: ['airport', 'planespotting'],
    source: yt('OUBslrCqREs'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'ap-miami',
    cityId: 'airports',
    name: 'Miami (MIA)',
    operator: 'Ozolio (Embassy Suites)',
    pageUrl: watch('q2Dxx5704fQ'),
    coords: [25.7959, -80.287],
    tags: ['airport', 'planespotting'],
    source: yt('q2Dxx5704fQ'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'ap-neworleans',
    cityId: 'airports',
    name: 'New Orleans (MSY)',
    operator: 'Flightradar24',
    pageUrl: watch('MH0_mPt-VXE'),
    coords: [29.9934, -90.258],
    tags: ['airport', 'planespotting'],
    source: yt('MH0_mPt-VXE'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'ap-reno',
    cityId: 'airports',
    name: 'Reno–Tahoe (RNO)',
    operator: 'Flightradar24',
    pageUrl: watch('fFl0qD108sk'),
    coords: [39.4991, -119.7681],
    tags: ['airport', 'planespotting'],
    source: yt('fFl0qD108sk'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'ap-montreal',
    cityId: 'airports',
    name: 'Montréal–Trudeau (YUL) — runway 24L',
    operator: 'Moore Equipment',
    pageUrl: watch('iJxeutc3QCI'),
    coords: [45.4706, -73.7408],
    tags: ['airport', 'planespotting'],
    source: yt('iJxeutc3QCI'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'ap-minneapolis',
    cityId: 'airports',
    name: 'Minneapolis–St. Paul (MSP)',
    operator: 'MNCam',
    pageUrl: watch('wWm-kbryDDQ'),
    coords: [44.8848, -93.2223],
    tags: ['airport', 'planespotting'],
    source: yt('wWm-kbryDDQ'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'ap-chicago-midway',
    cityId: 'airports',
    name: 'Chicago Midway (MDW)',
    operator: 'StreamTime LIVE',
    pageUrl: watch('67BCsiW-1Io'),
    coords: [41.7868, -87.7522],
    tags: ['airport', 'planespotting', '4K'],
    source: yt('67BCsiW-1Io'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'ap-manchester',
    cityId: 'airports',
    name: 'Manchester (MAN)',
    operator: 'AJTAviation',
    pageUrl: watch('BWi42mhP4cY'),
    coords: [53.3537, -2.275],
    tags: ['airport', 'planespotting'],
    source: yt('BWi42mhP4cY'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'ap-prague',
    cityId: 'airports',
    name: 'Prague (PRG) — Václav Havel',
    operator: 'SlowTV',
    pageUrl: watch('kuOmmVkOGN8'),
    coords: [50.1008, 14.26],
    tags: ['airport', 'planespotting'],
    source: yt('kuOmmVkOGN8'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'ap-lanzarote',
    cityId: 'airports',
    name: 'Lanzarote (ACE) — Canary Islands',
    operator: 'Flightradar24',
    pageUrl: watch('NtbquzembC8'),
    coords: [28.9455, -13.6052],
    tags: ['airport', 'planespotting'],
    source: yt('NtbquzembC8'),
    verifiedAt: '2026-08-10',
  },
  {
    id: 'ap-tenerife',
    cityId: 'airports',
    name: 'Tenerife South (TFS) — Canary Islands',
    operator: 'LanzaroteWebcam',
    pageUrl: watch('yrx0fvj-4QI'),
    coords: [28.0445, -16.5725],
    tags: ['airport', 'planespotting'],
    source: yt('yrx0fvj-4QI'),
    verifiedAt: '2026-08-10',
  },
  // Paris (CDG / Orly / Le Bourget): checked 2026-08-18 — no operator publishes
  // a permanent public camera at any of the three. The "CDG"/"ORY" YouTube live
  // streams are departure boards, not cameras, and the only spotting stream
  // found was a one-off broadcast from 2022. See docs/sources.md.
  // London Heathrow: no single permanent 24/7 video exists — the spotting
  // channels go live daily with a new video id. So these follow the CHANNEL's
  // current live stream (`channelId`), which survives the daily id change. They
  // are daytime feeds and show "offline" overnight (Heathrow has a night curfew).
  {
    id: 'ap-heathrow-flightfocus',
    cityId: 'airports',
    name: 'London Heathrow (LHR)',
    description: 'Daytime plane-spotting at Heathrow — follows the channel’s current live stream.',
    operator: 'Flight Focus 365',
    pageUrl: 'https://www.youtube.com/channel/UC6q_hfBThkGdmQ5Vb4kWjWA/live',
    coords: [51.47, -0.4543],
    tags: ['airport', 'planespotting', 'london'],
    source: { kind: 'youtube', channelId: 'UC6q_hfBThkGdmQ5Vb4kWjWA' },
    verifiedAt: '2026-08-10',
  },
  {
    id: 'ap-heathrow-adventures',
    cityId: 'airports',
    name: 'London Heathrow (LHR) — approach view',
    description: 'A second Heathrow angle — channel-follow live stream, offline overnight.',
    operator: 'Little & Great Adventures',
    pageUrl: 'https://www.youtube.com/channel/UCrRi157wAo3ibI1PUGWiFtQ/live',
    coords: [51.4706, -0.4619],
    tags: ['airport', 'planespotting', 'london'],
    source: { kind: 'youtube', channelId: 'UCrRi157wAo3ibI1PUGWiFtQ' },
    verifiedAt: '2026-08-10',
  },
]
